import { Button } from '../components/ui/button';
import { useDashboardProjects as useDashboardProjectsQuery, useDashboardComponents, useDashboardScores } from '../hooks/useDashboard';
import {
    VulnerabilityDistribution,
    LicenseRiskDistribution,
    OverallRiskDistribution,
    RiskLandscapeChart,
    TechnicalDebtChart,
    ComponentRiskHeatMap
} from '@/components/charts';
import {
    useOverallRiskDistribution,
    useVulnerabilityDistribution,
    useLicenseDistribution,
    SEVERITY_COLORS,

    getRiskCategoryForTitle
} from '@/components/charts/distributionUtils';

import { FindProjectsOptions } from '@/api';
import { StatCard, ChartCard } from '@/components/ui/card';
import { RiskGaugeCard } from '@/components/ui/risk-gauge-card';
import { RiskScoreInfoBar } from '@/components/ui/risk-score-info-bar';
import { DataTable } from '../components/ui/data-table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@radix-ui/react-progress';
import { getColorForTitle, getIconForTitle, getRiskScoreComments, toNumber } from '@/helpers/common';
import { useTheme } from 'next-themes';


interface DashboardProps {
    workgroupId: number;
    onNavigate?: (route: string, params?: any) => void;
}

const getScoreColorClass = (title: string, value: number): string => {
    const riskCategory = getRiskCategoryForTitle(title, value) as keyof typeof SEVERITY_COLORS;
    const riskColor = SEVERITY_COLORS[riskCategory];

    return riskColor;
};


export function Dashboard({ workgroupId, onNavigate }: DashboardProps) {
    const { theme, setTheme } = useTheme();

    const { data: projects = [], isLoading: projectsLoading, error: projectsError, refetch: fetchDashboardProjects } = useDashboardProjectsQuery({ workgroupId } as FindProjectsOptions);
    const { data: components = [], isLoading: componentsLoading, error: componentsError } = useDashboardComponents({ workgroupId } as FindProjectsOptions);
    const { data: dashboardScores, isLoading: scoresLoading } = useDashboardScores({ workgroupId });

    const loading = projectsLoading || componentsLoading || scoresLoading;

    const error = projectsError || componentsError;

    const getComparisonMeta = (current: number, baseline: number, lowerIsBetter = true) => {
        const delta = current - baseline;
        const absDelta = Math.abs(delta);

        if (absDelta < 0.01) {
            return {
                comparisonText: 'No change vs historical average',
                comparisonClassName: 'text-muted-foreground'
            };
        }

        const improved = lowerIsBetter ? delta < 0 : delta > 0;

        return {
            comparisonText: `${improved ? 'Improved' : 'Worsened'} by ${absDelta.toFixed(2)} vs historical average`,
            comparisonClassName: improved ? 'text-green-600' : 'text-red-600'
        };
    };

    const statCards: Array<{
        title: string;
        comment?: string;
        value: number | string;
        hint?: string;
        route?: string;
        comparisonText?: string;
        comparisonClassName?: string;
        disabled?: boolean;
    }> = [];
    const applicationData: Array<{
        id: number;
        name: string;
        description: string;
        projectCount: number;
        overallscore: number;
        vulnerabilityscore: number;
        licenseissuesfound: number;
        licensescore: number;
        operationalriskscore: number;
        businessriskscore: number;
        technicaldebtscore: number;
        totalComponents: number;
    }> = [];

    if (projects && projects.length > 0) {
        const applicationIds = new Set<number>();

        const summary = projects.reduce((acc: any, project: any) => {
            if (project.applicationid) {
                applicationIds.add(project.applicationid);
            }

            const scanCount = toNumber(project.total_scan_count);
            const componentCount = toNumber(project.total_component_count);
            const latestComponentCount = toNumber(project.latest_component_count);
            const uniqueComponentCount = toNumber(project.unique_component_count);

            const overallScore = toNumber(project.latest_overall_score ?? project.avg_overall_score);
            const vulnerabilityScore = toNumber(project.latest_vulnerability_score ?? project.avg_vulnerability_score);
            const licenseScore = toNumber(project.latest_license_score ?? project.avg_license_score);
            const operationalScore = toNumber(project.latest_operational_risk_score ?? project.avg_operational_risk_score);
            const businessRiskScore = toNumber(project.avg_business_risk_score);
            const technicalDebtScore = toNumber(project.latest_technical_debt_score ?? project.avg_technical_debt_score);

            const baselineOverallScore = toNumber(project.avg_overall_score ?? project.latest_overall_score);
            const baselineVulnerabilityScore = toNumber(project.avg_vulnerability_score ?? project.latest_vulnerability_score);
            const baselineLicenseScore = toNumber(project.avg_license_score ?? project.latest_license_score);
            const baselineOperationalScore = toNumber(project.avg_operational_risk_score ?? project.latest_operational_risk_score);
            const baselineTechnicalDebtScore = toNumber(project.avg_technical_debt_score ?? project.latest_technical_debt_score);

            const encryptionComponents = toNumber(project.encryption_count);
            const provenanceComponents = toNumber(project.provenance_count);

            acc.totalProjects += 1;
            acc.totalScans += scanCount;
            acc.totalComponents += componentCount;
            acc.totalLatestComponents += latestComponentCount;
            acc.totalUniqueComponents += uniqueComponentCount;

            acc.totalTechnicalDebt += technicalDebtScore;
            acc.totalBusinessRisk += businessRiskScore;
            acc.totalOverallScore += overallScore;
            acc.totalVulnerabilityScore += vulnerabilityScore;
            acc.totalLicenseScore += licenseScore;
            acc.totalOperationalScore += operationalScore;
            acc.baselineOverallScore += baselineOverallScore;
            acc.baselineVulnerabilityScore += baselineVulnerabilityScore;
            acc.baselineLicenseScore += baselineLicenseScore;
            acc.baselineOperationalScore += baselineOperationalScore;
            acc.baselineTechnicalDebtScore += baselineTechnicalDebtScore;

            acc.totalEncryptionComponents += encryptionComponents;
            acc.totalProvenanceComponents += provenanceComponents;

            return acc;
        }, {
            totalProjects: 0,
            totalScans: 0,
            totalComponents: 0,
            totalLatestComponents: 0,
            totalUniqueComponents: 0,
            totalTechnicalDebt: 0,
            totalBusinessRisk: 0,
            totalOverallScore: 0,
            totalVulnerabilityScore: 0,
            totalLicenseScore: 0,
            totalOperationalScore: 0,
            baselineOverallScore: 0,
            baselineVulnerabilityScore: 0,
            baselineLicenseScore: 0,
            baselineOperationalScore: 0,
            baselineTechnicalDebtScore: 0,
            totalEncryptionComponents: 0,
            totalProvenanceComponents: 0
        });

        const averageCurrentOverallScore = toNumber(dashboardScores?.overallScore);
        const averageCurrentTechnicalDebtScore = toNumber(dashboardScores?.technicalDebtScore);
        const averageCurrentOperationalScore = toNumber(dashboardScores?.operationalScore);
        const averageCurrentVulnerabilityScore = toNumber(dashboardScores?.vulnerabilityScore);
        const averageCurrentLicenseScore = toNumber(dashboardScores?.licenseScore);

        const averageBaselineOverallScore = summary.baselineOverallScore / summary.totalProjects;
        const averageBaselineTechnicalDebtScore = summary.baselineTechnicalDebtScore / summary.totalProjects;
        const averageBaselineOperationalScore = summary.baselineOperationalScore / summary.totalProjects;
        const averageBaselineVulnerabilityScore = summary.baselineVulnerabilityScore / summary.totalProjects;
        const averageBaselineLicenseScore = summary.baselineLicenseScore / summary.totalProjects;

        statCards.push(
            { title: 'Total Applications', value: applicationIds.size, hint: 'Count of applications that have at least one actively scanned project. One application can contain multiple projects.', route: 'applications' },
            { title: 'Total Projects', value: summary.totalProjects, hint: 'Count of non-empty projects with scan activity. One project can contain multiple scans or versions.', route: 'projects' },
            { title: 'Total Scans', value: Math.round(summary.totalScans), hint: 'Total number of scans run across all applications and projects.', route: 'scans' },
            { title: 'Total Components', value: Math.round(summary.totalComponents), hint: 'Total components detected across all scans.', route: 'components-hub' },
            { title: 'Total Encryption Algorithms', value: Math.round(summary.totalEncryptionComponents), hint: 'Total number of cryptographic algorithms detected across all scanned applications.', disabled: true },
            { title: 'Provenance', value: Math.round(summary.totalProvenanceComponents), hint: 'Number of component contributors whose geographic location could be determined from available metadata.', disabled: true },
            {
                title: 'Overall Risk Score',
                value: averageCurrentOverallScore.toFixed(2),
                hint: 'Organization-level risk score combining License, Vulnerability, and Operational risk signals. Higher score = higher combined exposure.',
                comment:getRiskScoreComments('overall_risk_score', averageCurrentOverallScore),
                ...getComparisonMeta(averageCurrentOverallScore, averageBaselineOverallScore),
                
            },

            {
                title: 'Total Technical Debt',
                value: averageCurrentTechnicalDebtScore.toFixed(2),
                hint: 'Reflects the effort required to remediate identified issues. Combines overall risk with business impact data collected from surveys.',
               comment:getRiskScoreComments('total_technical_debt', averageCurrentTechnicalDebtScore),
                ...getComparisonMeta(averageCurrentTechnicalDebtScore, averageBaselineTechnicalDebtScore)
            },
            {
                title: 'Operational Risk Score',
                value: averageCurrentOperationalScore.toFixed(2),
                hint: 'Reflects the presence of deprecated, unmaintained, or outdated components. Considers last update dates and maintainer deprecation or archival declarations.',
               comment:getRiskScoreComments('operational_risk_score', averageCurrentOperationalScore),
                ...getComparisonMeta(averageCurrentOperationalScore, averageBaselineOperationalScore)
            },
            {
                title: 'Vulnerability Risk Score',
                value: averageCurrentVulnerabilityScore.toFixed(2),
                hint: 'Reflects the presence of Critical and High severity vulnerabilities. Factors in exploit availability, EPSS likelihood, and survey inputs.',
               comment:getRiskScoreComments('vulnerability_risk_score', averageCurrentVulnerabilityScore),
                ...getComparisonMeta(averageCurrentVulnerabilityScore, averageBaselineVulnerabilityScore)
            },
            {
                title: 'License Risk Score',
                value: averageCurrentLicenseScore.toFixed(2),
                hint: 'Reflects the presence of Commercial or Strong Copyleft licenses across your applications. Critical licenses carry higher weight in the calculation.',
               comment:getRiskScoreComments('license_risk_score', averageCurrentLicenseScore),
                ...getComparisonMeta(averageCurrentLicenseScore, averageBaselineLicenseScore)
            },
            // { title: 'Business Risk', value: (summary.totalBusinessRisk / summary.totalProjects).toFixed(2), hint: 'Represents business impact derived from criticality and exposure. Overall project risk based on license, vulnerability, and operational scores.' },


        );

        const applicationMap = new Map<number, {
            id: number;
            name: string;
            description: string;
            projectCount: number;
            overallscore: number;
            vulnerabilityscore: number;
            licensescore: number;
            licenseissuesfound: number;
            operationalriskscore: number;
            businessriskscore: number;
            technicaldebtscore: number;
            totalComponents: number;
        }>();

        projects.forEach((project: any) => {
            if (!project.applicationid) {
                return;
            }

            const appId = project.applicationid;
            const metrics = {
                overallscore: toNumber(project.avg_overall_score),
                vulnerabilityscore: toNumber(project.avg_vulnerability_score),
                licenseissuesfound: toNumber(project.license_issues_found),
                licensescore: toNumber(project.avg_license_score),
                operationalriskscore: toNumber(project.avg_operational_risk_score),
                businessriskscore: toNumber(project.avg_business_risk_score),
                technicaldebtscore: toNumber(project.avg_technical_debt_score),
                totalComponents: toNumber(project.total_component_count),
            };

            if (applicationMap.has(appId)) {
                const existing = applicationMap.get(appId)!;
                const previousCount = existing.projectCount;
                const newCount = previousCount + 1;

                applicationMap.set(appId, {
                    ...existing,
                    projectCount: newCount,
                    overallscore: ((existing.overallscore * previousCount) + metrics.overallscore) / newCount,
                    vulnerabilityscore: ((existing.vulnerabilityscore * previousCount) + metrics.vulnerabilityscore) / newCount,
                    licenseissuesfound: ((existing.licenseissuesfound * previousCount) + metrics.licenseissuesfound) / newCount,
                    licensescore: ((existing.licensescore * previousCount) + metrics.licensescore) / newCount,
                    operationalriskscore: ((existing.operationalriskscore * previousCount) + metrics.operationalriskscore) / newCount,
                    businessriskscore: ((existing.businessriskscore * previousCount) + metrics.businessriskscore) / newCount,
                    technicaldebtscore: ((existing.technicaldebtscore * previousCount) + metrics.technicaldebtscore) / newCount,
                    totalComponents: existing.totalComponents + metrics.totalComponents,
                });
            } else {
                applicationMap.set(appId, {
                    id: appId,
                    name: project.application_name || project.name || `Application ${appId}`,
                    description: project.description || 'No description available',
                    projectCount: 1,
                    overallscore: metrics.overallscore,
                    vulnerabilityscore: metrics.vulnerabilityscore,
                    licenseissuesfound: metrics.licenseissuesfound,
                    operationalriskscore: metrics.operationalriskscore,
                    businessriskscore: metrics.businessriskscore,
                    technicaldebtscore: metrics.technicaldebtscore,
                    licensescore: metrics.licensescore,
                    totalComponents: metrics.totalComponents,
                });
            }
        });

        applicationData.push(...Array.from(applicationMap.values()).sort((a, b) => b.overallscore - a.overallscore));
    }

    const overallRiskData = useOverallRiskDistribution(components);
    const vulnerabilityData = useVulnerabilityDistribution(components);
    const licenseData = useLicenseDistribution(components);

    if (loading) {
        return (
            <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-muted-foreground">Loading dashboard data...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-red-600">{error.message}</div>
                </div>
                <div className="flex justify-center">
                    <Button onClick={() => fetchDashboardProjects()}>Retry</Button>
                </div>
            </div>
        );
    }

    const firstRowStats = statCards.slice(0, 6);
    const secondRowStats = statCards.slice(6);

    return (
        <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {firstRowStats.map((score) => {
                    return (<StatCard
                        key={score.title}
                        title={score.title}
                        value={score.value}
                        icon={getIconForTitle(score.title)}
                        color={getColorForTitle(score.title, statCards.findIndex(s => s.title === score.title))}
                        hint={score.hint}
                        comparisonText={score.comparisonText}
                        comparisonClassName={score.comparisonClassName}
                        onClick={score.route ? () => onNavigate && onNavigate(score.route) : undefined}
                        disabled={score.disabled}
                    />);
                })}
            </div>

            <RiskScoreInfoBar />
            {secondRowStats.length > 0 && (
                <div className="mt-4">

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {secondRowStats.map((score) => {
                            const numValue = typeof score.value === 'string' ? parseFloat(score.value) : score.value;
                            const riskCategory = getRiskCategoryForTitle(score.title, numValue) as keyof typeof SEVERITY_COLORS;
                            const riskColor = SEVERITY_COLORS[riskCategory];
                            const riskLabel = riskCategory.charAt(0).toUpperCase() + riskCategory.slice(1);

                            return (
                                <RiskGaugeCard
                                    key={score.title}
                                    title={score.title}
                                    value={score.value}
                                    riskColor={riskColor}
                                    riskLabel={riskLabel}
                                    hint={score.hint}
                                    comment={score.comment}
                                    comparisonText={score.comparisonText}
                                    comparisonClassName={score.comparisonClassName}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                <OverallRiskDistribution data={overallRiskData} onNavigate={onNavigate} />
                <VulnerabilityDistribution data={vulnerabilityData} onNavigate={onNavigate} />
                <LicenseRiskDistribution data={licenseData} onNavigate={onNavigate} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">

                <ComponentRiskHeatMap
                    data={applicationData}

                />
                <RiskLandscapeChart data={applicationData} />
            </div>


            {applicationData.length > 0 && (
                <div className="grid grid-cols-1 mt-4">
                    <DataTable
                        data={applicationData}
                        title="Application Details"
                        description="Comprehensive risk analysis for all applications"
                        searchable={false}
                        itemsPerPage={1000}
                        onRowClick={(app) => onNavigate && onNavigate('application-view-report', { applicationId: app.id })}
                        rowClassName={() => "cursor-pointer hover:bg-muted/50"}
                        columns={[
                            {
                                key: 'name',
                                header: 'Application Name',
                                align: 'left',
                                sortable: true,
                                render: (value) => (
                                    <span className="font-medium">{value}</span>
                                )
                            },
                            {
                                key: 'overallscore',
                                header: 'Overall Risk Score',
                                hint: 'Organization-level risk score combining License, Vulnerability, and Operational risk signals. Higher score = higher combined exposure.',
                                align: 'center',
                                sortable: true,
                                render: (value) => (
                                    <span className='font-medium' style={{ color: getScoreColorClass('Overall Risk Score', value) }}>
                                        {Number(value).toFixed(2)}
                                    </span>
                                )
                            },
                            {
                                key: 'vulnerabilityscore',
                                header: 'Vulnerability Score',
                                hint: 'Reflects the presence of Critical and High severity vulnerabilities. Factors in exploit availability, EPSS likelihood, and survey inputs.',
                                align: 'center',
                                sortable: true,
                                render: (value) => (
                                    <span className='font-medium' style={{ color: getScoreColorClass('Vulnerability Score', value) }}>
                                        {Number(value).toFixed(2)}
                                    </span>
                                )
                            },
                            {
                                key: 'licensescore',
                                header: 'License Risk Score',
                                hint: 'Reflects the presence of Commercial or Strong Copyleft licenses across your applications. Critical licenses carry higher weight in the calculation.',
                                align: 'center',
                                sortable: true,
                                render: (value) => (
                                    <span className='font-medium' style={{ color: getScoreColorClass('License Risk Score', value) }}>
                                        {Number(value).toFixed(2)}
                                    </span>
                                )
                            },
                            {
                                key: 'operationalriskscore',
                                header: 'Operational Risk Score',
                                hint: 'Reflects the presence of deprecated, unmaintained, or outdated components. Considers last update dates and maintainer deprecation or archival declarations.',
                                align: 'center',
                                sortable: true,
                                render: (value) => (
                                    <span className='font-medium' style={{ color: getScoreColorClass('Operational Risk Score', value) }}>
                                        {Number(value).toFixed(2)}
                                    </span>
                                )
                            },

                            {
                                key: 'technicaldebtscore',
                                header: 'Technical Debt',
                                hint: 'Reflects the effort required to remediate identified issues. Combines overall risk with business impact data collected from surveys.',
                                align: 'center',
                                sortable: true,
                                render: (value) => (
                                    <span className='font-medium' style={{ color: getScoreColorClass('Technical Debt', value) }}>
                                        {Number(value).toFixed(2)}
                                    </span>
                                )
                            },
                            // {
                            //     key: 'businessriskscore',
                            //     header: 'Business Impact',
                            //     align: 'center',
                            //     sortable: true,
                            //     render: (value) => (
                            //         <span className={value > 7 ? 'text-red-600' : value > 5 ? 'text-orange-600' : value > 3 ? 'text-yellow-600' : 'text-green-600'}>
                            //             {value}
                            //         </span>
                            //     )
                            // },
                            {
                                key: 'projectCount',
                                header: 'Projects',
                                align: 'center',
                                sortable: true,
                                render: (value) => (
                                    <span className="text-muted-foreground font-medium">
                                        {value}
                                    </span>
                                )
                            }
                        ]}
                    />

                </div>)}
        </div>
    );
}
